import { css } from '@emotion/react';
import ClickableContainer from 'components/clickable-container';
import React, { useState, useRef } from 'react';
import NvIcon from 'shared/components/nv-icon';
import { primary, white } from 'styles/global_defaults/colors';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';

export type InlineVideoPreviewProps = {
  videoSrc: string;
  thumbnailSrc?: string;
  title: string;
  width?: number;
  height?: number;
  dataQa?: string;
  dataQaId?: string;
  ariaLabel?: string;
};

const styles = (width: number, height: number) => css`
  aspect-ratio: 16/9;
  .thumbnail-wrapper {

    position: relative;
    cursor: pointer;
    transition: transform 0.2s;
    overflow: hidden;
    border-radius: ${quarterSpacing}px;
    width: ${width !== undefined ? `${width}px` : '100%'};
    height: ${height !== undefined ? `${height}px` : '100%'};
  }
  .thumbnail-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
  .thumbnail-wrapper:hover {
    transform: scale(1.02);
  }
  .play-button-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.9;
    transition: opacity 0.3s;
  }
  .play-button-overlay svg {
    width: 17%;
    height: auto;
    min-width: 24px;
    min-height: 24px;
  }
  .thumbnail-wrapper:hover .play-button-overlay {
    opacity: 1;
  }
  .play-button-bg {
    fill: rgba(0, 0, 0, 0.7);
    transition: fill 0.3s ease;
  }
  .thumbnail-wrapper:hover .play-button-bg {
    fill: ${primary};
  }
  .inline-video {
    width: 100%;
    aspect-ratio: 16/9;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  .close-btn {
    i {
      color: ${white} !important;
    }
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.6);
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 2;
    font-size: 18px;
  }
`;

const InlineVideoPreview = ({
  videoSrc,
  thumbnailSrc,
  title,
  width,
  height,
  dataQa,
  dataQaId,
  ariaLabel,
}: InlineVideoPreviewProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handleClose = () => {
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      css={styles(width, height)} className='position-relative'
    >
      {!isPlaying ? (
        <div
          className='thumbnail-wrapper'
          onClick={handlePlay}
          data-qa={dataQa}
          data-qa-id={dataQaId}
          aria-label={ariaLabel}
        >
          <img src={thumbnailSrc} alt={title} className='thumbnail-image' />
          <div className='play-button-overlay'>
            <svg width='40' height='40' viewBox='0 0 24 24' fill='none'>
              <circle
                cx='12'
                cy='12'
                r='11'
                fill='rgba(0,0,0,0.7)'
                className='play-button-bg'
              />
              <path d='M9 7L17 12L9 17V7Z' fill='white' />
            </svg>
          </div>
        </div>
      ) : (
        <>
          <ClickableContainer className='close-btn' onClick={handleClose}>
            <NvIcon icon='close' size='xss-smallest' />
          </ClickableContainer>
          <video
            ref={videoRef}
            src={videoSrc}
            controls
            autoPlay
            className='inline-video'
            title={title}
          />
        </>
      )}
    </div>
  );
};

export default InlineVideoPreview;
